import { useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { progressState } from '@/lib/practice/dotProgress';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import { useShellSlots } from '@/contexts/ShellSlotsContext';
import {
  FloatingEndButton,
  FloatingMomentum,
  HelpButton,
  ProgressFooter,
  SessionTimer,
  useEndOnEsc,
  type Shortcut,
} from '@/components/features/mindspace/practice/runners/RunnerChrome';

/**
 * Both runners' frame. Desktop keeps the floating chrome (End top-left,
 * timer top-right, momentum top-centre, dot progress at the foot, the
 * keyboard-shortcut help bottom-right).
 *
 * A phone is a focus mode, the way a session is meant to be: the page
 * header and the app navbar step away (the page does that), and the frame
 * draws one bar across the top - End, a progress bar with "3 / 12", and the
 * focus timer if one is running (it lives in the navbar otherwise) - with the
 * runner's actions pinned at the bottom in the thumb's reach (`footer`). No
 * help button: it lists keyboard shortcuts, and it sat over the quiz's
 * answers. No dots at the foot: the bar says where you are.
 */
export function RunnerChromeFrame({
  onEnd,
  startedAt,
  index,
  total,
  momentum,
  shortcuts,
  footer,
  children,
}: {
  onEnd: () => void;
  startedAt: number | null;
  index: number;
  total: number;
  momentum?: ReactNode;
  shortcuts: Shortcut[];
  /** Phone only: pinned under the content. Desktop runners draw it inline. */
  footer?: ReactNode;
  children: ReactNode;
}) {
  // Scoped to this copy: the layout mounts the chrome twice, and an ungated
  // Escape ended the session from the hidden copy as well.
  const chromeRef = useRef<HTMLDivElement | null>(null);
  useEndOnEsc(onEnd, chromeRef);
  const phone = useMediaQuery('(max-width: 767.98px)');

  if (phone) {
    return (
      <div ref={chromeRef} className='relative flex h-full flex-col'>
        <PhoneSessionBar onEnd={onEnd} index={index} total={total} />
        {momentum && (
          <div className='flex h-8 flex-shrink-0 items-center justify-center'>
            {momentum}
          </div>
        )}
        {children}
        {footer && (
          <div className='flex-shrink-0 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2'>
            {footer}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={chromeRef} className='relative flex h-full flex-col'>
      <FloatingEndButton onEnd={onEnd} />
      <SessionTimer startedAt={startedAt} />
      <FloatingMomentum>{momentum}</FloatingMomentum>
      {children}
      <ProgressFooter index={index} total={total} />
      <HelpButton shortcuts={shortcuts} />
    </div>
  );
}

/** ✕ · progress bar · "3 / 12" · the focus pill, if a block is running. */
function PhoneSessionBar({
  onEnd,
  index,
  total,
}: {
  onEnd: () => void;
  index: number;
  total: number;
}) {
  const { navbarAccessory } = useShellSlots();
  const { position, label } = progressState(index, total);
  // Filled through the card you are on: "1 / 12" over an empty bar read as
  // nothing started.
  const percent = total > 0 ? (position / total) * 100 : 0;
  return (
    <div className='flex flex-shrink-0 items-center gap-2 pl-1 pr-4 pt-[max(0.5rem,env(safe-area-inset-top))]'>
      <button
        type='button'
        onClick={onEnd}
        aria-label='End session'
        className='flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      >
        <X className='h-5 w-5' aria-hidden='true' />
      </button>
      <div
        role='progressbar'
        aria-label={label}
        aria-valuemin={1}
        aria-valuemax={Math.max(total, 1)}
        aria-valuenow={position}
        className='h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted'
      >
        <div
          className='h-full rounded-full bg-primary motion-safe:transition-all motion-safe:duration-300'
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className='flex-shrink-0 font-mono text-sm font-medium tabular-nums text-foreground'>
        {position} / {total}
      </span>
      {navbarAccessory}
    </div>
  );
}
